import * as Menu from '@radix-ui/react-dropdown-menu'
import { GallerySection } from './GallerySection'
import { BOARD_TITLE_MAX, type BoardSummary } from '@shared/api'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, MoreHorizontal, Pencil, Plus, Trash2, Upload } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { AppHeader } from '@/components/AppHeader'
import { Button, IconButton } from '@/components/Button'
import { EditableTitle } from '@/components/EditableTitle'
import { MenuContent, menuItemClass } from '@/components/Menu'
import { useI18n } from '@/i18n/i18n'
import { api, ApiError } from '@/lib/api'
import { formatUsd } from '@/session/describe'

export function Boards() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const qc = useQueryClient()
  // Outside apps make and change boards too (MCP): the list follows while it is in view.
  const boards = useQuery({
    queryKey: ['boards'],
    queryFn: api.boards.list,
    refetchOnWindowFocus: true,
    refetchInterval: 15_000,
  })
  const fileInput = useRef<HTMLInputElement>(null)

  // A new account starts with the example board, made once by the server.
  // Asked once per visit to this page, when the list turns out empty.
  const askedExample = useRef(false)
  const [seeding, setSeeding] = useState(false)
  const empty = boards.data?.length === 0
  useEffect(() => {
    if (!empty || askedExample.current) return
    askedExample.current = true
    setSeeding(true)
    api.boards
      .example(locale)
      .then(({ board }) => (board ? qc.invalidateQueries({ queryKey: ['boards'] }) : undefined))
      .catch(() => undefined)
      .finally(() => {
        setSeeding(false)
      })
  }, [empty, locale, qc])
  /** Show a new board at once; a list read still in flight must not overwrite it. */
  const added = async (b: BoardSummary) => {
    await qc.cancelQueries({ queryKey: ['boards'] })
    qc.setQueryData<BoardSummary[]>(['boards'], (old) => [
      b,
      ...(old ?? []).filter((x) => x.id !== b.id),
    ])
    void qc.invalidateQueries({ queryKey: ['boards'] })
  }

  const importBoard = useMutation({
    mutationFn: (file: File) => api.boards.import(file),
    onSuccess: async (b) => {
      await added(b)
      toast(t('boards.imported', { title: b.title }), {
        action: {
          label: t('boards.open'),
          onClick: () => {
            void navigate(`/b/${b.id}`)
          },
        },
      })
    },
    onError: (e) => {
      toast.error(
        e instanceof ApiError && e.code === 'invalid_board_file'
          ? t('boards.importInvalid', { reason: e.detail ?? '' })
          : e instanceof ApiError && e.code === 'too_large'
            ? t('boards.importTooLarge')
            : t('error.generic'),
      )
    },
  })
  const chooseFile = () => {
    fileInput.current?.click()
  }
  const importInput = (
    <input
      ref={fileInput}
      type="file"
      accept=".moot,.zip,application/zip"
      className="hidden"
      aria-hidden="true"
      tabIndex={-1}
      onChange={(e) => {
        const file = e.target.files?.[0]
        e.target.value = ''
        if (file) importBoard.mutate(file)
      }}
    />
  )

  const create = useMutation({
    mutationFn: () => api.boards.create(t('boards.untitled')),
    onSuccess: async (b) => {
      await added(b)
      void navigate(`/b/${b.id}`)
    },
    onError: () => toast.error(t('error.generic')),
  })

  return (
    <div className="min-h-full">
      <AppHeader />
      <main className="mx-auto w-full max-w-5xl px-4 pt-10 pb-24 sm:px-6">
        <div className="mb-6 flex items-end gap-2">
          <h1 className="text-[26px] font-semibold tracking-[-0.025em]">{t('boards.title')}</h1>
          <span className="flex-1" />
          {importInput}
          <Button onClick={chooseFile} disabled={importBoard.isPending}>
            <Upload size={15} />
            {t('boards.import')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              create.mutate()
            }}
            disabled={create.isPending}
          >
            <Plus size={16} strokeWidth={2.2} />
            {t('boards.new')}
          </Button>
        </div>

        {(boards.isPending || (empty && seeding)) && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-32 animate-pulse rounded-lg bg-surface-2" />
            ))}
          </div>
        )}

        {empty && !seeding && (
          <div className="mt-16 flex flex-col items-center text-center">
            <div className="grid size-14 place-items-center rounded-2xl bg-ai-soft text-ai">
              <Plus size={24} />
            </div>
            <h2 className="mt-4 text-[17px] font-semibold">{t('boards.empty.title')}</h2>
            <p className="mt-1 max-w-sm text-sm text-balance text-muted">
              {t('boards.empty.body')}
            </p>
            <div className="mt-6 flex gap-2">
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  create.mutate()
                }}
              >
                {t('boards.new')}
              </Button>
              <Button size="lg" onClick={chooseFile}>
                <Upload size={15} />
                {t('boards.import')}
              </Button>
            </div>
          </div>
        )}

        {boards.data && boards.data.length > 0 && (
          <ul
            aria-label={t('boards.title')}
            className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4"
          >
            {boards.data.map((b) => (
              <BoardTile key={b.id} board={b} />
            ))}
          </ul>
        )}
        <GallerySection />
      </main>
    </div>
  )
}

function BoardTile({ board }: { board: BoardSummary }) {
  const { t, relativeTime } = useI18n()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [renaming, setRenaming] = useState(false)
  const renameRequested = useRef(false)

  const patch = (fn: (list: BoardSummary[]) => BoardSummary[]) => {
    qc.setQueryData<BoardSummary[]>(['boards'], (old) => fn(old ?? []))
  }

  const rename = (title: string) => {
    patch((l) => l.map((b) => (b.id === board.id ? { ...b, title } : b)))
    api.boards.rename(board.id, title).catch(() => {
      toast.error(t('error.generic'))
      void qc.invalidateQueries({ queryKey: ['boards'] })
    })
  }

  const remove = async () => {
    patch((l) => l.filter((b) => b.id !== board.id))
    try {
      await api.boards.remove(board.id)
    } catch {
      toast.error(t('error.generic'))
      void qc.invalidateQueries({ queryKey: ['boards'] })
      return
    }
    toast(t('boards.deleted', { title: board.title }), {
      action: {
        label: t('boards.undo'),
        onClick: () => {
          void api.boards
            .restore(board.id)
            .then(() => qc.invalidateQueries({ queryKey: ['boards'] }))
        },
      },
    })
  }

  const open = () => {
    if (!renaming) void navigate(`/b/${board.id}`)
  }

  return (
    <li className="group relative">
      <div
        role="link"
        tabIndex={0}
        onClick={open}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target === e.currentTarget) open()
        }}
        className="flex h-32 flex-col rounded-lg border border-line bg-surface p-4 shadow-sm transition-[box-shadow,transform,border-color] duration-200 ease-(--ease-spring) hover:-translate-y-0.5 hover:border-line-strong hover:shadow-md"
      >
        <div className="relative mb-3 h-9 flex-none overflow-hidden" aria-hidden="true">
          <span className="absolute top-1 left-0 h-6 w-14 rounded-[5px] border border-line bg-surface-2" />
          <span className="absolute top-3 left-11 h-6 w-12 rounded-[5px] bg-ai-soft" />
          <span className="absolute top-0 left-26 h-5 w-9 rounded-[5px] border border-line bg-surface-2" />
        </div>
        <EditableTitle
          value={board.title}
          editing={renaming}
          onEditingChange={setRenaming}
          onCommit={rename}
          maxLength={BOARD_TITLE_MAX}
          className="truncate pr-8 text-[15px] font-medium tracking-[-0.01em]"
          inputClassName="text-[15px] font-medium"
        />
        <span className="mt-auto flex items-baseline justify-between gap-2 text-xs text-muted">
          <span className="truncate">
            {t('boards.updated', { time: relativeTime(board.updatedAt) })}
          </span>
          {(board.costMicros ?? 0) > 0 && (
            <span className="flex-none tabular-nums" title={t('boards.cost')}>
              {formatUsd(board.costMicros ?? 0)}
            </span>
          )}
        </span>
      </div>
      <div className="absolute top-2.5 right-2.5 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 has-data-[state=open]:opacity-100">
        <Menu.Root>
          <Menu.Trigger asChild>
            <IconButton label={t('boards.actions')}>
              <MoreHorizontal size={16} />
            </IconButton>
          </Menu.Trigger>
          <MenuContent
            onCloseAutoFocus={(e) => {
              if (renameRequested.current) e.preventDefault()
              renameRequested.current = false
            }}
          >
            <Menu.Item
              className={menuItemClass}
              onSelect={() => {
                renameRequested.current = true
                setRenaming(true)
              }}
            >
              <Pencil size={14} className="text-muted" />
              {t('boards.rename')}
            </Menu.Item>
            <Menu.Item
              className={menuItemClass}
              onSelect={() => {
                const a = document.createElement('a')
                a.href = api.boards.exportUrl(board.id)
                a.download = ''
                a.click()
              }}
            >
              <Download size={14} className="text-muted" />
              {t('boards.export')}
            </Menu.Item>
            <Menu.Separator className="my-1 h-px bg-line" />
            <Menu.Item
              className={`${menuItemClass} text-danger`}
              onSelect={() => {
                void remove()
              }}
            >
              <Trash2 size={14} />
              {t('boards.delete')}
            </Menu.Item>
          </MenuContent>
        </Menu.Root>
      </div>
    </li>
  )
}
